import { apiFetch } from '@/lib/api';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw, Film } from 'lucide-react';

interface Shot {
  shot_id: string;
  scene_number: number;
  shot_type: string;
  description: string;
  dialog_line_ids?: string[];
  vo_line_ids?: string[];
  estimated_duration_seconds?: number;
  veo_duration?: number;
}

interface ShotEditorProps {
  productionId: string;
  shotPlan: { shots: Shot[] } | null;
  onReassemble?: () => void;
}

export default function ShotEditor({ productionId, shotPlan, onReassemble }: ShotEditorProps) {
  const { t } = useTranslation('production');
  const [shots, setShots] = useState<(Shot & { enabled: boolean })[]>([]);
  const [reassembling, setReassembling] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (shotPlan?.shots) {
      setShots(shotPlan.shots.map((s) => ({ ...s, enabled: true })));
    }
  }, [shotPlan]);

  if (!shots.length) return null;

  const moveShot = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= shots.length) return;
    const updated = [...shots];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setShots(updated);
  };

  const toggleShot = (index: number) => {
    const updated = [...shots];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };
    setShots(updated);
  };

  const resetOrder = () => {
    if (shotPlan?.shots) {
      setShots(shotPlan.shots.map((s) => ({ ...s, enabled: true })));
      setResult(null);
    }
  };

  const handleReassemble = async () => {
    setReassembling(true);
    setResult(null);

    const enabledShots = shots.filter((s) => s.enabled);

    try {
      const resp = await apiFetch(`/api/reassemble/${productionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shots: enabledShots }),
      });

      if (resp.ok) {
        const data = await resp.json();
        setResult(`Re-assembled: ${data.duration_seconds?.toFixed(1)}s, ${data.clips_used} clips`);
        onReassemble?.();
      } else {
        setResult('Re-assembly failed');
      }
    } catch {
      setResult('Re-assembly error');
    } finally {
      setReassembling(false);
    }
  };

  const enabledCount = shots.filter((s) => s.enabled).length;
  const totalDuration = shots
    .filter((s) => s.enabled)
    .reduce((sum, s) => sum + (s.veo_duration || s.estimated_duration_seconds || 4), 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Shot Editor</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {enabledCount}/{shots.length} shots &middot; ~{totalDuration}s
            </Badge>
            <Button variant="ghost" size="sm" onClick={resetOrder} title={t('editor.reset')}>
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1 max-h-[400px] overflow-y-auto mb-3">
          {shots.map((shot, i) => (
            <div
              key={`${shot.shot_id}-${i}`}
              className={`flex items-center gap-2 p-1.5 rounded border text-xs transition-opacity ${
                shot.enabled ? 'border-border bg-background' : 'border-border/50 bg-muted/30 opacity-50'
              }`}
            >
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => moveShot(i, -1)}
                  disabled={i === 0}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-20"
                >
                  <ArrowUp className="h-3 w-3" />
                </button>
                <button
                  onClick={() => moveShot(i, 1)}
                  disabled={i === shots.length - 1}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-20"
                >
                  <ArrowDown className="h-3 w-3" />
                </button>
              </div>

              <img
                src={`/api/assets/${productionId}/keyframes/${shot.shot_id}.png`}
                alt={shot.shot_id}
                className="w-16 h-9 object-cover rounded border border-border flex-shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="font-medium">{shot.shot_id}</span>
                  <Badge variant="secondary" className="text-[10px] px-1 py-0">
                    {shot.shot_type}
                  </Badge>
                  <span className="text-muted-foreground ml-auto">
                    {shot.veo_duration || shot.estimated_duration_seconds || '?'}s
                  </span>
                </div>
                <div className="text-muted-foreground truncate">{shot.description}</div>
              </div>

              <button
                onClick={() => toggleShot(i)}
                className="text-muted-foreground hover:text-foreground flex-shrink-0"
                title={shot.enabled ? t('editor.disable') : t('editor.enable')}
              >
                {shot.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleReassemble} disabled={reassembling || enabledCount === 0} className="flex-1">
            <Film className="h-4 w-4 mr-2" />
            {reassembling ? t('editor.reassembling') : t('editor.reassemble')}
          </Button>
        </div>

        {result && <div className="mt-2 text-xs text-muted-foreground">{result}</div>}
      </CardContent>
    </Card>
  );
}
